import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/lib/api";

import { createEarlyPaymentRequest, fetchOpenEarlyPaymentRequest } from "./earlyPaymentRequestsApi";

export function useApInvoiceEarlyPaymentRequest(invoiceId: string): {
  hidden: boolean;
  label: string;
  disabled: boolean;
  errorMessage: string | null;
  onSelect: () => void;
} {
  const trimmedId = invoiceId.trim();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const openQuery = useQuery({
    queryKey: ["accounting", "early-payment-request", trimmedId],
    queryFn: () => fetchOpenEarlyPaymentRequest(trimmedId),
    enabled: trimmedId !== "",
  });
  const createMutation = useMutation({
    mutationFn: () => createEarlyPaymentRequest(trimmedId),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["accounting", "early-payment-request", trimmedId] });
      await queryClient.invalidateQueries({ queryKey: ["accounting", "early-payment-requests"] });
      if (!result.already_open) {
        navigate(`/accounting/early-payment-requests?request=${encodeURIComponent(result.request.id)}`);
      }
    },
  });

  const openRequest = openQuery.data?.request ?? null;
  const errorMessage =
    createMutation.error instanceof ApiError
      ? createMutation.error.message
      : createMutation.error instanceof Error
        ? createMutation.error.message
        : null;

  return {
    hidden: trimmedId === "",
    label: openRequest != null
      ? "Early payment requested"
      : createMutation.isPending
        ? "Requesting"
        : "Request early payment",
    disabled: openRequest == null && createMutation.isPending,
    errorMessage,
    onSelect: () => {
      if (openRequest != null) {
        navigate(`/accounting/early-payment-requests?request=${encodeURIComponent(openRequest.id)}`);
        return;
      }
      createMutation.mutate();
    },
  };
}
